import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { AuthProvider, useAuth } from './AuthContext';
import { API_ROOT as API } from './config';
import { useDemandPoints, PAGE_SIZE } from './DemandPointContext';
import LoginScreen from './LoginScreen';
import ProfileModal from './ProfileModal';
import useGeolocation from './hooks/useGeolocation';
import BuildingForm from './components/building/BuildingForm';
import BuildingDetail from "./components/building/BuildingDetail";
import Navigation from "./components/Navigation";
import {Dashboard} from "./components/dashboard/Dashboard";

// Main App Component with Authentication
function MainApp() {
  const NEW_BUILDING_FEATURE = false;
  const { user, updateActivity } = useAuth();
  const {
    buildings,
    loading: buildingsLoading,
    pageInfo,
    loadDemandPoints,
  } = useDemandPoints();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  // ================================================================================
  // STATE MANAGEMENT WITH PERSISTENCE
  // ================================================================================
  
  // Load last view from localStorage or default to dashboard
  const [currentView, setCurrentView] = useState(() => {
    const savedView = localStorage.getItem('fastplanet_last_view');
    return savedView || 'dashboard';
  });

  // Auto-save current view to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fastplanet_last_view', currentView);
  }, [currentView]);

  // ================================================================================
  // AUTO-SAVE FUNCTIONALITY FOR FORM DATA
  // ================================================================================
  
  // Load draft building data from localStorage
  const [draftBuildingData, setDraftBuildingData] = useState(() => {
    const savedDraft = localStorage.getItem('fastplanet_building_draft');
    return savedDraft ? JSON.parse(savedDraft) : null;
  });

  // Auto-save building form data
  const saveBuildingDraft = useCallback((formData) => {
    const draftData = {
      ...formData,
      savedAt: Date.now(),
      id: 'draft_' + Date.now() // Temporary ID for draft
    };
    localStorage.setItem('fastplanet_building_draft', JSON.stringify(draftData));
    setDraftBuildingData(draftData);
    console.log('Building draft auto-saved:', draftData);
  }, []);

  // Clear building draft
  const clearBuildingDraft = useCallback(() => {
    localStorage.removeItem('fastplanet_building_draft');
    setDraftBuildingData(null);
    console.log('Building draft cleared');
  }, []);

  const switchToView = useCallback((newView) => {
    setSearchTerm('');
    setRadiusSearchEnabled(false);
    setCurrentView(newView);
  }, [currentView]);

  const [selectedBuilding, setSelectedBuilding] = useState(() => {
    // Try to restore selected building from localStorage
    const savedBuilding = localStorage.getItem('fastplanet_selected_building');
    if (savedBuilding) {
      try {
        return JSON.parse(savedBuilding);
      } catch (error) {
        console.warn('Failed to parse saved building:', error);
        localStorage.removeItem('fastplanet_selected_building');
      }
    }
    return null;
  });

  // Handle case where we only have building ID stored
  useEffect(() => {
    if (!selectedBuilding) {
      const savedBuildingId = localStorage.getItem('fastplanet_selected_building_id');
      if (savedBuildingId && buildings.length > 0) {
        const building = buildings.find(b => b.id === savedBuildingId);
        if (building) {
          setSelectedBuilding(building);
          localStorage.removeItem('fastplanet_selected_building_id'); // Clean up
        }
      }
    }
  }, [buildings, selectedBuilding]);

  // Update selectedBuilding with fresh data when buildings are loaded
  useEffect(() => {
    if (selectedBuilding && buildings.length > 0) {
      const freshBuilding = buildings.find(b => b.id === selectedBuilding.id);
      if (freshBuilding && JSON.stringify(freshBuilding) !== JSON.stringify(selectedBuilding)) {
        console.log('🔄 Updating selectedBuilding with fresh data from backend');
        setSelectedBuilding(freshBuilding);
      }
    }
  }, [buildings]);

  // Save selected building to localStorage whenever it changes
  useEffect(() => {
    if (selectedBuilding) {
      try {
        // Only save essential building data to avoid quota exceeded error
        const buildingToSave = {
          id: selectedBuilding.id,
          demand_point_id: selectedBuilding.demand_point_id,
          building_type: selectedBuilding.building_type,
          address: selectedBuilding.address,
          total_floors: selectedBuilding.total_floors,
          residential_units: selectedBuilding.residential_units,
          commercial_units: selectedBuilding.commercial_units,
          floors: selectedBuilding.floors,
          photos: selectedBuilding.photos || []
        };
        localStorage.setItem('fastplanet_selected_building', JSON.stringify(buildingToSave));
      } catch (error) {
        console.warn('Failed to save building with photos to localStorage:', error);
        // Try without photos if photos are too large
        try {
          const buildingToSaveWithoutPhotos = {
            id: selectedBuilding.id,
            demand_point_id: selectedBuilding.demand_point_id,
            building_type: selectedBuilding.building_type,
            address: selectedBuilding.address,
            total_floors: selectedBuilding.total_floors,
            residential_units: selectedBuilding.residential_units,
            commercial_units: selectedBuilding.commercial_units,
            floors: selectedBuilding.floors
          };
          localStorage.setItem('fastplanet_selected_building', JSON.stringify(buildingToSaveWithoutPhotos));
          console.log('✅ Saved building without photos to localStorage');
        } catch (fallbackError) {
          console.warn('Failed to save building without photos to localStorage:', fallbackError);
          // If still too large, just save the ID
          try {
            localStorage.setItem('fastplanet_selected_building_id', selectedBuilding.id);
          } catch (finalError) {
            console.error('Even basic building save failed:', finalError);
          }
        }
      }
    } else {
      localStorage.removeItem('fastplanet_selected_building');
      localStorage.removeItem('fastplanet_selected_building_id');
    }
  }, [selectedBuilding]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Demand point id set explicitly when user clicks "Details".
  // Using a plain number/string primitive avoids object-reference issues.
  const [selectedDemandPointId, setSelectedDemandPointId] = useState(null);
  const [detailDemandPoint, setDetailDemandPoint]         = useState(null);
  const [detailLoading, setDetailLoading]                 = useState(false);

  useEffect(() => {
    if (!selectedDemandPointId) {
      setDetailDemandPoint(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailDemandPoint(null);
    axios.get(`${API}/demand-points/${selectedDemandPointId}`)
      .then(res => {
        if (!cancelled) setDetailDemandPoint(res.data);
      })
      .catch(err => console.error('[detail] fetch failed:', err))
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDemandPointId]);
  const [radiusSearchEnabled, setRadiusSearchEnabled] = useState(false);
  const [currentUserLocation, setCurrentUserLocation] = useState(null);
  const { requestLocation } = useGeolocation();

  // Handle changes in search criteria and trigger loadDemandPoints
  useEffect(() => {
    if (!user) return;

    const timeoutId = setTimeout(() => {
      const search_text = searchTerm.trim().length >= 3 ? searchTerm.trim() : null;
      const lat = radiusSearchEnabled && currentUserLocation ? currentUserLocation.latitude : null;
      const lon = radiusSearchEnabled && currentUserLocation ? currentUserLocation.longitude : null;
      const radius = radiusSearchEnabled ? 200 : null;

      loadDemandPoints(0, PAGE_SIZE, search_text, lat, lon, radius);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [user, searchTerm, radiusSearchEnabled, currentUserLocation, loadDemandPoints, currentView]);

  // Helper: open building detail and trigger fresh demand-point fetch
  const openBuildingDetail = useCallback((building) => {
    const dpId = building?.demand_point_id ?? building?._demandPoint?.id ?? null;
    setSelectedBuilding(building);
    setSelectedDemandPointId(dpId);
    switchToView('building-detail');
  }, [switchToView]);

  // Handle radius search toggle
  const handleRadiusSearchToggle = async () => {
    updateActivity();
    
    if (!radiusSearchEnabled) {
      // Turning ON radius search - request location
      try {
        console.log('Starting radius search - requesting location...');
        
        // Show loading feedback
        const originalButton = document.querySelector('[onclick*="handleRadiusSearchToggle"]');
        if (originalButton) {
          originalButton.textContent = 'GPS wird abgerufen...';
        }
        
        const location = await requestLocation();
        console.log('Location received for radius search:', location);
        
        setCurrentUserLocation(location);
        setRadiusSearchEnabled(true);
        
        // Show success message
        alert(`Umkreissuche aktiviert!\nIhre Position: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}\nGenauigkeit: ±${Math.round(location.accuracy)}m`);
        
      } catch (error) {
        console.error('Radius search GPS error:', error);
        
        const errorMessage = typeof error === 'string' ? error : error.message || 'Unbekannter GPS-Fehler';
        
        alert(`GPS-Fehler bei Umkreissuche: ${errorMessage}\n\nTipp: Stellen Sie sicher, dass:\n• Standort-Berechtigung erteilt ist\n• GPS/Standortdienste aktiviert sind\n• Sie eine stabile Internetverbindung haben`);
        
        setRadiusSearchEnabled(false);
        setCurrentUserLocation(null);
      }
    } else {
      // Turning OFF radius search
      console.log('Turning off radius search');
      setRadiusSearchEnabled(false);
      setCurrentUserLocation(null);
    }
  };

  // Filter buildings based on search criteria (deprecated client-side filter, now using buildings from context)
  const getFilteredBuildings = () => {
    return buildings;
  };

  // DELETE /api/demand-points/{id} is not implemented on the backend.
  // Deletion of demand points is not supported in this version.
  const handleDeleteBuilding = useCallback((building) => {
    alert(
      'Das Löschen von Gebäuden ist in dieser Version nicht verfügbar.\n' +
      'Bitte wenden Sie sich an Ihren Administrator.'
    );
  }, []);

  const handleBuildingSubmit = async (buildingData) => {
    const { saveBuildingWithStableIds } = await import('./building-persistence.js');
    
    console.log('🚀 BUILDING_SUBMIT: Starting save with stable IDs');
    console.log('📊 BUILDING_SUBMIT: Data summary:', {
      isUpdate: !!selectedBuilding,
      buildingId: selectedBuilding?.id,
      hasFloors: !!buildingData.floors,
      floorCount: buildingData.floors?.length || 0,
      address: `${buildingData.address?.street || ''} ${buildingData.address?.house_number ?? ''}`
    });
    
    try {
      const buildingDataWithContext = {
        ...buildingData,
        _demandPoint: selectedBuilding?._demandPoint
      };

      // Use the new stable ID-based persistence
      const savedBuilding = await saveBuildingWithStableIds(buildingDataWithContext, selectedBuilding);
      
      console.log('✅ BUILDING_SUBMIT: Save successful!', {
        buildingId: savedBuilding.id,
        method: selectedBuilding ? 'UPDATE' : 'CREATE'
      });
      
      clearBuildingDraft();
      
      await loadDemandPoints();

      switchToView('buildings');
      setSelectedBuilding(null);
      
    } catch (error) {
      console.error('💥 BUILDING_SUBMIT: Save failed:', error);
      
      let errorMessage = 'Fehler beim Speichern des Gebäudes';
      if (error.message.includes('401')) {
        errorMessage = 'Authentifizierung fehlgeschlagen - bitte erneut anmelden';
      } else if (error.message.includes('422')) {
        errorMessage = 'Ungültige Gebäudedaten - bitte überprüfen Sie Ihre Eingaben';
      } else if (error.message.includes('404')) {
        errorMessage = 'Gebäude nicht gefunden - möglicherweise bereits gelöscht';
      } else {
        errorMessage += ': ' + (error.message || 'Unbekannter Fehler');
      }
      
      alert(errorMessage);
    }
  };

  const renderNavigation = () => {
    return (
        <Navigation
            currentView={currentView}
            user={user}
            switchToView={switchToView}
            updateActivity={updateActivity}
            selectedBuilding={selectedBuilding}
            setSelectedBuilding={setSelectedBuilding}
            setProfileModalOpen={setProfileModalOpen}
        />);
  }

  const renderDashboard = () => {
    return <Dashboard
        currentUserLocation={currentUserLocation}
        setCurrentUserLocation={setCurrentUserLocation}
        onBuildingDetail={openBuildingDetail}
    />;
  };

  const renderBuildings = () => {
    const filteredBuildings = getFilteredBuildings();
    
    return (
      <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gebäudedetails</h2>
        {NEW_BUILDING_FEATURE && (<button
            onClick={() => {
              setSelectedBuilding(null);
              switchToView('building-form');
            }}
            className="btn-primary"
        >
          Neues Gebäude
        </button>)}
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔍 Suche
            </label>
            <input
              type="text"
              placeholder="PLZ, Straßenname, Ort oder Ortsteil eingeben..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Radius Search Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRadiusSearchToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  radiusSearchEnabled ? 'bg-orange-600' : 'bg-gray-200'
                }`}
                disabled={false}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    radiusSearchEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-gray-700">
                📍 Umkreissuche (200m)
              </span>
              {radiusSearchEnabled && currentUserLocation && (
                <span className="text-xs text-green-600">
                  ✅ Aktiv (±{Math.round(currentUserLocation.accuracy)}m)
                </span>
              )}
              {radiusSearchEnabled && !currentUserLocation && (
                <span className="text-xs text-red-500">
                  ⏳ GPS-Position wird ermittelt...
                </span>
              )}
            </div>
          </div>
            
          {/* Search Results Info */}
          <div className="text-sm text-gray-500">
            {filteredBuildings.length} von {buildings.length} auf dieser Seite
            {pageInfo.totalElements > 0 && (
              <span className="ml-1 text-gray-400">
                ({pageInfo.totalElements} gesamt)
              </span>
            )}
          </div>
        </div>
      </div>

      {buildingsLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="loading-spinner"></div>
          <span className="ml-2">Lade Gebäude...</span>
        </div>
      ) : buildings.length === 0 ? (
        <div className="card text-center py-12">
          <h3 className="text-lg font-semibold mb-4">Keine Gebäude vorhanden</h3>
          <p className="text-gray-600 mb-6">
            Erfassen Sie Ihr erstes Gebäude, um zu beginnen.
          </p>
          <button
            disabled={true}
            onClick={() => switchToView('building-form')}
            className="btn-primary"
          >
            Erstes Gebäude erfassen
          </button>
        </div>
      ) : filteredBuildings.length === 0 ? (
        <div className="card text-center py-12">
          <h3 className="text-lg font-semibold mb-4">Keine Gebäude gefunden</h3>
          <p className="text-gray-600 mb-6">
            Ihre Suche ergab keine Treffer. Ändern Sie die Suchkriterien oder deaktivieren Sie die Umkreissuche.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setRadiusSearchEnabled(false);
            }}
            className="btn-secondary"
          >
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredBuildings.map((building) => (
            // ...existing building card JSX...
            <div key={building.id} className="card p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <h3 className="text-lg font-semibold flex items-center mb-1">
                    {building.address?.street || 'Keine Straße'} {building.address?.house_number ?? ''}{building.address?.house_number_suffix || ''}
                  </h3>

                  {/* Postal address preview */}
                  <p className="text-gray-600 mb-0.5">
                    {building.address?.zip_code || ''} {building.address?.city || ''}
                    {building.address?.district && <span className="text-gray-500"> ({building.address.district})</span>}
                  </p>
                  {building.address?.federal_state && (
                    <p className="text-sm text-gray-500 mb-1">{building.address.federal_state}</p>
                  )}

                  {/* Demand Point UUID */}
                  {(building._demandPoint?.externalUuid ?? building._demandPoint?.external_uuid ?? building._demandPoint?.externalId) && (
                    <p className="text-xs text-gray-400 font-mono mb-2 truncate" title="Demand Point UUID">
                      DP: {building._demandPoint?.externalUuid ?? building._demandPoint?.external_uuid ?? building._demandPoint?.externalId}
                    </p>
                  )}

                  <p className="text-sm text-gray-600 mt-2 mb-2">
                    {building.total_floors || 0} Ebenen • {building.residential_units || 0} WE • {building.business_units || 0} GE
                  </p>
                  <p className="text-sm text-gray-500 mb-3">
                    Typ: {building.building_type === 'residential' ? 'Wohngebäude' :
                          building.building_type === 'commercial' ? 'Geschäftsgebäude' : 'Mischgebäude'}
                  </p>
                  {building.created_at && (
                    <div className="text-xs text-gray-400 mt-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <p>
                        <span className="font-medium">Erstanlage:</span> 
                        <span className="ml-1">
                          {new Date(building.created_at).toLocaleDateString('de-DE', { 
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                        {building.updated_at && building.updated_at !== building.created_at && (
                          <span className="ml-6">
                            <span className="font-medium">Letzte Änderung:</span>
                            <span className="ml-1">
                              {new Date(building.updated_at).toLocaleDateString('de-DE', { 
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </span>
                        )}
                      </p>
                      <FontAwesomeIcon
                        icon={faTrashCan} 
                        className="cursor-pointer transition-colors duration-200 text-sm hover:text-red-600" 
                        style={{ color: '#C5703D' }}
                        onMouseEnter={(e) => { e.target.style.color = '#dc2626'; }}
                        onMouseLeave={(e) => { e.target.style.color = '#C5703D'; }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteBuilding(building);
                        }}
                        title="Gebäude löschen"
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={() => openBuildingDetail(building)}
                    className="btn-details px-4 py-2 min-w-[100px]"
                  >
                    Details
                  </button>
                  <button
                    onClick={() => { setSelectedBuilding(building); switchToView('building-form'); }}
                    className="btn-primary px-4 py-2 min-w-[100px]"
                  >
                    Bearbeiten
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination controls */}
      {!buildingsLoading && (
        <div className="flex items-center justify-between mt-6 px-1">
          {/* Page info */}
          <p className="text-sm text-gray-600">
            Seite <span className="font-medium">{pageInfo.page + 1}</span> von{' '}
            <span className="font-medium">{pageInfo.totalPages}</span>
            {pageInfo.totalElements > 0 && (
              <span className="ml-2 text-gray-400">
                ({pageInfo.totalElements} gesamt)
              </span>
            )}
          </p>

          {/* Prev / page numbers / Next */}
          <div className="flex items-center gap-1">
            {/* Previous */}
            <button
              onClick={() => loadDemandPoints(pageInfo.page - 1, pageInfo.size)}
              disabled={pageInfo.first || buildingsLoading}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ‹
            </button>

            {/* Page number buttons (show up to 5 around current) */}
            {Array.from({ length: pageInfo.totalPages }, (_, i) => i)
              .filter((i) => Math.abs(i - pageInfo.page) <= 2)
              .map((i) => (
                <button
                  key={i}
                  onClick={() => loadDemandPoints(i, pageInfo.size)}
                  disabled={buildingsLoading}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    i === pageInfo.page
                      ? 'border-orange-500 bg-orange-500 text-white font-semibold'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:cursor-not-allowed`}
                >
                  {i + 1}
                </button>
              ))}

            {/* Next */}
            <button
              onClick={() => loadDemandPoints(pageInfo.page + 1, pageInfo.size)}
              disabled={pageInfo.last || buildingsLoading}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ›
            </button>
          </div>

          {/* Page size selector */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Pro Seite:</span>
            <select
              value={pageInfo.size}
              onChange={(e) => loadDemandPoints(0, parseInt(e.target.value))}
              disabled={buildingsLoading}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              {[10, 20, 50, 100].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
  };

  const renderBuildingDetail = () => {
    return <BuildingDetail
        selectedBuilding={selectedBuilding}
        detailDemandPoint={detailDemandPoint}
        detailLoading={detailLoading}
        switchToView={switchToView}
    />;
  };

  // ================================================================================
  // BUILDING FORM DRAFT RESTORATION
  // ================================================================================
  
  const renderBuildingForm = () => {
    return (
      <div className="max-w-4xl mx-auto">
        {/* Building Form Component */}
        <BuildingForm
          building={selectedBuilding}
          onSubmit={(buildingData) => {
            // Save draft before submitting
            if (saveBuildingDraft && !selectedBuilding) {
              saveBuildingDraft(buildingData);
            }
            handleBuildingSubmit(buildingData);
          }}
          onCancel={() => switchToView('building-detail')}
          onDraftSave={saveBuildingDraft} // Pass draft save function to form
        />
      </div>
    );
  };

  if (error && !buildings.length) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="card max-w-md text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Fehler beim Laden</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {renderNavigation()}
      
      {currentView === 'dashboard' && renderDashboard()}
      {currentView === 'buildings' && renderBuildings()}
      {currentView === 'building-form' && renderBuildingForm()}
      {currentView === 'building-detail' && renderBuildingDetail()}
      
      <ProfileModal 
        isOpen={profileModalOpen} 
        onClose={() => setProfileModalOpen(false)} 
      />
    </div>
  );
}

// App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// Content component that handles authentication state
function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-orange-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Lädt...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  return <MainApp />;
}

export default App;